import { useEffect, useMemo, useState } from 'react'
import type { Position, SlamPoint } from '../../robot-position-setup/types'

interface UsePositionSelectionStateOptions {
    positions: Position[]
    activeTab: string
    isAddingLocation: boolean
    positionMatchEps: number
}

const getPositionPoint = (
    position: Pick<Position, 'x' | 'y' | 'rawX' | 'rawY'>,
): SlamPoint => ({
    x: position.rawX ?? position.x,
    y: position.rawY ?? position.y,
})

export function usePositionSelectionState({
    positions,
    activeTab,
    isAddingLocation,
    positionMatchEps,
}: UsePositionSelectionStateOptions) {
    const [selectedPositionId, setSelectedPositionId] = useState<string>('')
    const [selectedCoordinate, setSelectedCoordinate] = useState<SlamPoint | null>(
        null,
    )
    const [positionPanelTab, setPositionPanelTab] = useState<'info' | 'schedule'>(
        'info',
    )
    const [directionRotateMode, setDirectionRotateMode] = useState(false)

    useEffect(() => {
        if (positions.length === 0) {
            setSelectedPositionId('')
            setSelectedCoordinate(null)
            setDirectionRotateMode(false)
            return
        }

        if (
            selectedPositionId &&
            !positions.some((position) => position.id === selectedPositionId)
        ) {
            setSelectedPositionId('')
        }
    }, [positions, selectedPositionId])

    useEffect(() => {
        if (!selectedPositionId) return
        const selected = positions.find(
            (position) => position.id === selectedPositionId,
        )
        if (!selected) return

        setSelectedCoordinate(getPositionPoint(selected))
    }, [positions, selectedPositionId])

    useEffect(() => {
        if (!selectedPositionId) {
            setDirectionRotateMode(false)
        }
    }, [selectedPositionId])

    useEffect(() => {
        if (!selectedCoordinate || isAddingLocation) return

        const hasAny = positions.some((position) => {
            const point = getPositionPoint(position)
            return (
                Math.abs(point.x - selectedCoordinate.x) <= positionMatchEps &&
                Math.abs(point.y - selectedCoordinate.y) <= positionMatchEps
            )
        })

        if (!hasAny) {
            setSelectedCoordinate(null)
        }
    }, [positions, positionMatchEps, selectedCoordinate, isAddingLocation])

    const selectedPosition = useMemo(
        () => positions.find((position) => position.id === selectedPositionId) ?? null,
        [positions, selectedPositionId],
    )

    const positionsAtSelectedCoordinate = useMemo(() => {
        if (!selectedCoordinate) return [] as Position[]

        const matched = positions.filter((position) => {
            const point = getPositionPoint(position)
            return (
                Math.abs(point.x - selectedCoordinate.x) <= positionMatchEps &&
                Math.abs(point.y - selectedCoordinate.y) <= positionMatchEps
            )
        })

        return matched.filter((position) => position.id !== 'home')
    }, [positions, positionMatchEps, selectedCoordinate])
    const showPositionPanel =
        !!selectedCoordinate || !!selectedPositionId || isAddingLocation

    const visibleMarkers = useMemo(() => {
        const baseMarkers = positions
            .filter((position) => position.id !== 'home')
            .map((position) => {
                const point = getPositionPoint(position)
                return {
                    ...position,
                    selected:
                        position.id === selectedPositionId ||
                        (!!selectedCoordinate &&
                            Math.abs(point.x - selectedCoordinate.x) <= positionMatchEps &&
                            Math.abs(point.y - selectedCoordinate.y) <= positionMatchEps),
                }
            })

        if (!selectedPositionId) return baseMarkers

        return baseMarkers.filter((position) => position.id === selectedPositionId)
    }, [positions, positionMatchEps, selectedCoordinate, selectedPositionId])

    const directionOverlayMarkerId = useMemo(() => {
        if (activeTab !== '2' || !directionRotateMode) return null
        if (!selectedPositionId) return null
        return selectedPositionId
    }, [activeTab, directionRotateMode, selectedPositionId])

    const directionOverlayYaw = useMemo(() => {
        if (directionRotateMode) {
            return typeof selectedPosition?.yaw === 'number' ? selectedPosition.yaw : null
        }
        return null
    }, [directionRotateMode, selectedPosition])

    const handleMarkerClick = (positionId: string) => {
        if (positionId === 'home') {
            setSelectedPositionId('home')
            setSelectedCoordinate(null)
            setPositionPanelTab('info')
            return
        }

        const clickedPosition = positions.find(
            (position) => position.id === positionId,
        )
        if (!clickedPosition) return
        if (clickedPosition.id === 'home') return

        const clickedPoint = getPositionPoint(clickedPosition)
        const coordinateMatches = positions.filter((position) => {
            const point = getPositionPoint(position)
            return (
                Math.abs(point.x - clickedPoint.x) <= positionMatchEps &&
                Math.abs(point.y - clickedPoint.y) <= positionMatchEps
            )
        })

        setSelectedCoordinate(clickedPoint)
        setSelectedPositionId(coordinateMatches.length === 1 ? positionId : '')
        setPositionPanelTab('info')
    }

    const handleSelectPositionFromCoordinate = (positionId: string) => {
        setSelectedPositionId(positionId)
        setPositionPanelTab('info')
    }

    const handleEditPositionDirection = (positionId: string) => {
        setSelectedPositionId(positionId)
        setDirectionRotateMode(true)
    }

    const handleClosePositionDetail = (isEditingMode: boolean, initialPositions: typeof positions, setPositions: React.Dispatch<React.SetStateAction<typeof positions>>) => {
        if (isEditingMode && selectedPosition) {
            const originalPos = initialPositions.find(p => p.id === selectedPosition.id)
            if (originalPos) {
                setPositions((prev) =>
                    prev.map((p) => (p.id === selectedPosition.id ? { ...originalPos } : p))
                )
            }
        }
        setSelectedPositionId('')
        setPositionPanelTab('info')
        setDirectionRotateMode(false)
    }

    return {
        selectedPositionId,
        setSelectedPositionId,
        selectedCoordinate,
        setSelectedCoordinate,
        positionPanelTab,
        setPositionPanelTab,
        directionRotateMode,
        setDirectionRotateMode,
        selectedPosition,
        positionsAtSelectedCoordinate,
        showPositionPanel,
        visibleMarkers,
        directionOverlayMarkerId,
        directionOverlayYaw,
        handleMarkerClick,
        handleSelectPositionFromCoordinate,
        handleEditPositionDirection,
        handleClosePositionDetail,
    }
}
